import { Link } from 'react-router-dom';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { heroTake } from '../../domain/cars/carSchema';
import { LockIcon } from '../icons/ControlIcons';
import { artSource } from './carArt';

/**
 * A car that has not been won yet (SPEC-010 PR-10.5).
 *
 * The counterpart to `CarCard`, and the reason it is a separate component
 * rather than a modifier on that one: the two cards are made of different
 * facts. An open card is a NAME, a tagline and a photograph; a locked one is a
 * shape, a year and a promise, and the whole point of it is what it withholds.
 * A `isLocked` branch through `CarCard` would be two components sharing one
 * name and one stylesheet, each getting in the other's way.
 *
 * THE SILHOUETTE IS THE REAL ARTWORK, blacked out in CSS. A second rendered
 * asset per car would be a build step, a tree the content build would have to
 * own by name, and a file that goes stale the day the art is recut — where
 * `brightness(0)` over the same `<img>` costs nothing and cannot disagree with
 * the picture it is made from. It is also the better drawing: a Countach in
 * silhouette is still unmistakably a Countach to somebody who knows one, which
 * is the wink, and to everybody else it is a shape with a target on it.
 *
 * THE NAME IS WITHHELD AND THE YEAR AND THE RARITY ARE NOT. A locked card that
 * said nothing would be a grey rectangle in a gallery of photographs; one that
 * said everything would make the ceremony a formality. The year and the ribbon
 * are what a reader needs in order to want it.
 *
 *
 * The SIDE profile, not the catalogue's front view.
 *
 * The shelf's open cards draw whichever take the schema calls the catalogue one,
 * and for every car in this pack that is the front three-quarter — a composed
 * picture with its own lit ground baked into the rectangle. Turned into a
 * silhouette it becomes exactly that: a rectangle. The side profile is a cut-out
 * on transparency, so what is left when the pixels are taken to one value is the
 * shape of the car and nothing else, which is the only version of this card
 * worth drawing.
 *
 *
 * The card links to the COMPETITION rather than to the car. It is the only
 * thing on this card a reader can act on, so it is what the tap does — and a
 * link to a screen that would tell them the same thing again would be a tap
 * that went nowhere.
 */
export default function CarLockedCard({ car }) {
    const { localeCode } = useLocale();
    const t = useTranslate();
    const art = artSource(car, heroTake(car));

    return (
        <article className="locked-card">
            <Link className="locked-card__link" to={`/${localeCode}/duel`}>
                <span className="locked-card__stage">
                    {art ? (
                        <img
                            className="locked-card__art"
                            src={art.src}
                            srcSet={art.srcSet}
                            /* The same ladder as `CarCard.js`, because this is
                               the same shelf at the same widths — `lockedCard.css`
                               follows `garage.css` column for column, and the two
                               declarations have to follow each other with it. */
                            sizes="(min-width: 1920px) 405px, (min-width: 1440px) 335px, (min-width: 1024px) 360px, (min-width: 640px) 300px, 92vw"
                            /* Decorative twice over: the heading beside it says
                               what this card is, and the picture itself has been
                               deliberately reduced to a shape. Announcing "a
                               black rectangle" would be worse than silence. */
                            alt=""
                            /* Always below the open shelf, which is at least
                               twelve cards tall. */
                            loading="lazy"
                            decoding="async"
                        />
                    ) : null}

                    {/* What tells a withheld picture apart from a failed one. A
                        silhouette with no lock on it reads as an image that did
                        not load, and a reader who thinks the app is broken does
                        not go and play for it. */}
                    <span className="locked-card__lock" aria-hidden="true">
                        <LockIcon size={28} />
                    </span>

                    <span className={`rarity-ribbon rarity-ribbon--${car.rarity} locked-card__rarity`}>
                        {t(`cars.rarity.${car.rarity}`)}
                    </span>
                </span>

                <span className="locked-card__hairline heat-hairline" aria-hidden="true" />

                <span className="locked-card__head">
                    {/* A heading, so the locked shelf is a navigable list of
                        things rather than a run of anonymous links — and one
                        level below `CarCard`'s, because these cards sit under a
                        band heading of their own and the open ones do not. */}
                    <h3 className="locked-card__name">{t('collection.locked')}</h3>
                    <span className="locked-card__year figure">{car.year}</span>
                </span>

                {/* Not a button — the whole card is already the link, and a
                    control inside an anchor is a control the anchor swallows.
                    It is drawn as one because that is what it does. */}
                <span className="locked-card__action">{t('collection.action')}</span>
            </Link>
        </article>
    );
}
